import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  useNavigate,
  useSearchParams as useRouterSearchParams,
} from 'react-router-dom'

import * as styles from './SearchPanel.module.scss'

import { PATH } from '../../../constants'
import {
  PathParams,
  SearchParam,
  useCreateNextPath,
  useProperties,
  usePropertyApi,
  usePublication,
  useRouteParams,
  useSearchQueryParams,
} from '../../../hooks'
import { useAirlineTicketUrl } from '../../../hooks/useAirlineTicketUrl'
import i18n from '../../../i18n'
import { formatJstDate, isTowerEleven } from '../../../utils'
import { Button, Col, Container, Row, Text } from '../../basics'
import {
  CalendarModal,
  FlightTicketModal,
  PropertyListModal,
  RoomSearchModal,
} from '../../modals'
import { Panel } from '../Panel'

const translateNamespace = 'components.Panel'

type UseNavigateRoomsOptions = {
  /**
   * Destination path to navigate to when search params change.
   * Defaults to `PATH.rooms`. Set to e.g. `PATH.plan.rooms` to keep the
   * `:plan` segment in the URL after re-searching.
   */
  destinationPath?: string
  /**
   * When `true`, the navigation merges the *current* values of every search
   * key (`check_in`, `check_out`, `rooms`, `adults`, `children`, `infants`)
   * from {@link useSearchQueryParams} with the partial update coming from the
   * modal — so that `hasFullSearch` becomes `true` even if the user only
   * touched one modal. Defaults to `false` (current behavior).
   */
  fillAllSearchKeys?: boolean
}

export const useNavigateRooms = ({
  destinationPath = PATH.rooms,
  fillAllSearchKeys = false,
}: UseNavigateRoomsOptions = {}) => {
  const navigate = useNavigate()
  const { onCreateNextPath } = useCreateNextPath()
  const { params: currentSearch } = useSearchQueryParams()
  // Raw URL params (no default-value back-fill) — used by fillAllSearchKeys
  // to decide which keys the user has actually set, vs. which are stale
  // defaults that should NOT silently make hasFullSearch=true.
  const [rawSearchParams] = useRouterSearchParams()

  const onParamsChange = useCallback(
    (pathParams: PathParams, queryParams: SearchParam) => {
      // Empty-landing detection: when the URL has *none* of the required
      // search keys, the user just hit a bare /:property/:plan/rooms link and
      // is touching a modal for the first time. In that case it's safe (and
      // desirable for UX) to back-fill every key from the current defaults so
      // `hasFullSearch` flips to true on the first submit. Compare to a
      // *partial* URL (e.g. `?check_in=...` only) which we must respect — see
      // Codex review round 7 — because the user actively chose those keys
      // and a silent +1-day completion would lie to them.
      const REQUIRED_KEYS = [
        'check_in',
        'check_out',
        'rooms',
        'adults',
        'children',
        'infants',
      ] as const
      const hasAnyRequiredKey = REQUIRED_KEYS.some((k) =>
        rawSearchParams.has(k)
      )

      let merged: SearchParam
      if (!fillAllSearchKeys) {
        merged = queryParams
      } else if (hasAnyRequiredKey) {
        // Partial URL path: only persist a key from currentSearch when the
        // raw URL already contains it. Otherwise the default-value back-fill
        // from useSearchQueryParams would re-introduce keys the user never
        // touched (e.g. a check_out auto-derived as check_in+1), which would
        // trip hasFullSearch and trigger priced fetches against unselected
        // dates. See Codex review round 7 / spec §5.1.
        merged = {
          ...(rawSearchParams.has('check_in') && {
            checkInDate: currentSearch.checkInDate,
          }),
          ...(rawSearchParams.has('check_out') && {
            checkOutDate: currentSearch.checkOutDate,
          }),
          ...(rawSearchParams.has('rooms') && {
            roomCount: currentSearch.roomCount,
          }),
          ...(rawSearchParams.has('adults') && {
            numberOfAdults: currentSearch.numberOfAdults,
          }),
          ...(rawSearchParams.has('children') && {
            numberOfChildren: currentSearch.numberOfChildren,
          }),
          ...(rawSearchParams.has('infants') && {
            numberOfInfants: currentSearch.numberOfInfants,
          }),
          // The modal submit always wins for the keys it carries.
          ...queryParams,
        }
      } else {
        // Empty-landing path: nothing in the URL yet, so this submit is the
        // user's first interaction. Promote the guest / room defaults so the
        // URL is well-formed, but only promote the default check-in/check-out
        // dates when THIS submit actually carried them (i.e. the calendar modal
        // was used). Otherwise a guest/room-only change would back-fill dates
        // the user never picked, flipping hasFullSearch to true and showing
        // prices for unselected dates. See Codex P2 (round 11).
        const submitHasDates =
          queryParams.checkInDate != null || queryParams.checkOutDate != null
        merged = {
          ...(submitHasDates && {
            checkInDate: currentSearch.checkInDate,
            checkOutDate: currentSearch.checkOutDate,
          }),
          roomCount: currentSearch.roomCount,
          numberOfAdults: currentSearch.numberOfAdults,
          numberOfChildren: currentSearch.numberOfChildren,
          numberOfInfants: currentSearch.numberOfInfants,
          ...queryParams,
        }
      }

      navigate(
        onCreateNextPath(destinationPath, {
          pathParams,
          queryParams: merged,
        })
      )
      window.scrollTo(0, 0)
    },
    [
      navigate,
      onCreateNextPath,
      destinationPath,
      fillAllSearchKeys,
      currentSearch,
      rawSearchParams,
    ]
  )

  return onParamsChange
}

type PanelItemProps = {
  label: React.ReactNode
  value: React.ReactNode
  action?: () => void
}

const PanelItem: React.FC<PanelItemProps> = ({ label, value, action }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.panelItem}>
      <Container>
        <Row>
          <Col pc={9} sp={9}>
            <Text size={12}>{label}</Text>
            <Text size={14} weight="bold" pcAlign="left" spAlign="left">
              {value}
            </Text>
          </Col>

          {action && (
            <Col pc={3} sp={3}>
              <div className={styles.button}>
                <Button
                  display="inlineBlock"
                  htmlType="button"
                  outline
                  size="small"
                  variant="secondary"
                  onClick={action}
                >
                  {t(`${translateNamespace}.change`)}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  )
}

export type SearchPanelProps = {
  /**
   * Where to navigate after the user changes a search parameter (date /
   * room count / pax). Defaults to `PATH.rooms`. Pass
   * `PATH.plan.rooms` from the secret-URL flow so the `:plan` segment
   * stays in the URL.
   *
   * NOTE: property changes always navigate to `PATH.rooms`, regardless of
   * this prop, because the old `:plan` segment would otherwise leak to a
   * different property and 404. See docs/specs/2026-05-28-secret-plan-design.md §5.5(a-2).
   */
  destinationPath?: string
  /**
   * Force every search key into the URL on submit (using the current values
   * as defaults). Needed by the secret-URL flow so `hasFullSearch` becomes
   * `true` even when the user only touches the calendar modal.
   */
  fillAllSearchKeys?: boolean
  /**
   * When set (the plan-first `/:property/:plan/rooms` flow), show a read-only
   * "プラン" row with the fixed plan's name, mirroring how the room type name
   * is shown elsewhere. The plan is fixed, so this row has no change button.
   */
  planName?: string
}

export const SearchPanel: React.FC<SearchPanelProps> = ({
  destinationPath,
  fillAllSearchKeys,
  planName,
}) => {
  // Plan-first pages keep the `:property/:plan` pair fixed. Derive this from the
  // synchronously-known `destinationPath` rather than the asynchronously-loaded
  // `planName` — otherwise the property "変更" action would briefly render (and
  // could drop the fixed-plan context) while `getPlanWithRooms` is still loading
  // or when the plan has no display name (Codex P2 / 3360298871).
  const isPlanFixed = destinationPath === PATH.plan.rooms
  const [propertyIsOpen, setPropertyIsOpen] = useState(false)
  const [calendarIsOpen, setCalendarIsOpen] = useState(false)
  const [roomIsOpen, setRoomIsOpen] = useState(false)
  const [flightTicketIsOpen, setFlightTicketIsOpen] = useState(false)
  const { t } = useTranslation()
  const { property: propertySlug } = useRouteParams()
  const { data: property } = usePropertyApi(propertySlug ?? '')
  const publication = usePublication(i18n.language, property?.publications)
  const onParamsChange = useNavigateRooms({
    destinationPath,
    fillAllSearchKeys,
  })
  // Property changes must always escape back to PATH.rooms — keeping a stale
  // `:plan` segment (e.g. from the secret URL) would 404 on the new property.
  const onPropertyChange = useNavigateRooms()

  const properties = useProperties().properties.map((p) => ({
    shortName: p.shortName ?? '',
    slug: p.slug ?? '',
    images: p.images,
    insideFvillage: p.insideFvillage,
  }))

  const { params } = useSearchQueryParams()
  const {
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfChildren,
    numberOfInfants,
    roomCount,
  } = params

  const withAirlineTicketUrl = useAirlineTicketUrl(
    property?.withAirlineTicketUrl
  )

  const image = useMemo(() => property?.images?.[0]?.url, [property?.images])

  return (
    <React.Fragment>
      <Panel
        cover={{
          name: publication?.name ?? '',
          image: image ?? '/img/hotel01.jpg',
          desc: t(`${translateNamespace}.action.room`),
        }}
        label={t(`${translateNamespace}.label.search`)}
        items={[
          <PanelItem
            label={t(`${translateNamespace}.property`)}
            value={publication?.name ?? ''}
            // On the plan-first page the property is fixed by the shared plan
            // URL — changing it would invalidate the `:plan` segment. Hide the
            // "変更" action so the property can't be swapped here. Gated on the
            // synchronous `isPlanFixed` (not the async `planName`) so the action
            // never flashes during load (Codex P2 / 3360298871).
            action={isPlanFixed ? undefined : () => setPropertyIsOpen(true)}
          />,
          ...(isPlanFixed
            ? [
                <PanelItem
                  label={t(`${translateNamespace}.plan`)}
                  value={planName ?? ''}
                />,
              ]
            : []),
          <PanelItem
            label={t(`${translateNamespace}.checkIn`)}
            value={formatJstDate(
              checkInDate,
              i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
            )}
            action={() => setCalendarIsOpen(true)}
          />,
          <PanelItem
            label={t(`${translateNamespace}.checkOut`)}
            value={formatJstDate(
              checkOutDate,
              i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
            )}
            action={() => setCalendarIsOpen(true)}
          />,
          <PanelItem
            label={t(`${translateNamespace}.roomCount`)}
            value={roomCount + t(`${translateNamespace}.roomUnit`)}
            action={() => setRoomIsOpen(true)}
          />,
          <PanelItem
            label={t(`${translateNamespace}.peopleCount`)}
            value={
              <Trans
                i18nKey={`${translateNamespace}.guests`}
                value={{
                  numberOfAdults,
                  numberOfChildren,
                  numberOfInfants,
                }}
              >
                {{ numberOfAdults }} adults
                <br />
                {{ numberOfChildren }} children
                <br />
                {{ numberOfInfants }} infants
              </Trans>
            }
            action={() => setRoomIsOpen(true)}
          />,
        ]}
      />
      {withAirlineTicketUrl && (
        <React.Fragment>
          <div className={styles.reservationButtonWrapper}>
            <Button
              htmlType="button"
              size="full"
              variant="tertiary"
              bold={!!property?.withAirlineTicketUrl}
              onClick={() => setFlightTicketIsOpen(true)}
            >
              {t(`${translateNamespace}.withFlight`)}
            </Button>
          </div>
          <div className={styles.notice}>
            <Text size={12}>{t(`${translateNamespace}.withFlightNote`)}</Text>
          </div>
          <FlightTicketModal
            linkUrl={withAirlineTicketUrl}
            isOpen={flightTicketIsOpen}
            onClose={() => setFlightTicketIsOpen(false)}
          />
        </React.Fragment>
      )}
      {/*
        幼児の寝具・追加料金・定員扱いは SuiteBook 側のプラン/部屋設定に依存する
        ため、全施設で固定表示すると誤表示になる。RoomSearchModal の幼児注釈
        （PR #1929）と同じく tower eleven hotel のみに限定する。
      */}
      {isTowerEleven(propertySlug) && (
        <div className={styles.notice}>
          <Text size={12}>{t(`${translateNamespace}.infantNotice`)}</Text>
        </div>
      )}
      {property?.insideFvillage && property?.officialUrl && (
        <div className={styles.notice}>
          <Text size={12}>
            {t(`${translateNamespace}.notice`)}
            <br />
            <a href={property.officialUrl} target="_blank" rel="noreferrer">
              {t(`${translateNamespace}.propertyDetail`, {
                name: publication?.shortName,
              })}
            </a>
          </Text>
        </div>
      )}
      <PropertyListModal
        isOpen={propertyIsOpen}
        onClose={() => setPropertyIsOpen(false)}
        onNext={(selectedProperty) => {
          setPropertyIsOpen(false)
          // `room` is a property-scoped room slug; carrying it over to a
          // different property would filter the new property's list down to a
          // slug it doesn't have, yielding an empty result (Codex P2 / FV-5).
          // `fromPlanRooms` is a plan-first origin marker that must not leak
          // into a subsequent room-first booking, or RegistrationNewFrame's
          // Back would mis-route to the plan-room page (Codex P2 / 3360088459).
          // Pass empty/false so updateSearchParams() actively deletes both keys
          // from the current URL (an `undefined` would be skipped and let the
          // stale values survive).
          onPropertyChange(
            { property: selectedProperty.slug },
            { ...params, room: '', fromPlanRooms: false }
          )
        }}
        defaultProperty={{
          shortName: publication?.name ?? '',
          slug: property?.slug ?? '',
          images: image ? [image] : [],
        }}
        properties={properties}
        nextButtonText={t(`${translateNamespace}.search`)}
      />
      <CalendarModal
        propertyName={publication?.name ?? ''}
        checkInParam={checkInDate}
        checkOutParam={checkOutDate}
        isOpen={calendarIsOpen}
        onClose={() => setCalendarIsOpen(false)}
        onSearch={(checkIn, checkOut) => {
          onParamsChange(
            { property: property?.slug },
            {
              checkInDate: checkIn,
              checkOutDate: checkOut,
            }
          )
          setCalendarIsOpen(false)
        }}
        key={`${checkInDate}${checkOutDate}`}
      />
      <RoomSearchModal
        isOpen={roomIsOpen}
        onClose={() => setRoomIsOpen(false)}
        propertyName={publication?.shortName ?? ''}
        officialUrl={property?.officialUrl ?? ''}
        propertySlug={property?.slug}
        roomParam={roomCount}
        adultParam={numberOfAdults}
        childParam={numberOfChildren}
        infantParam={numberOfInfants}
        checkIn={checkInDate}
        checkOut={checkOutDate}
        onSearch={(rooms, adults, children, infant) => {
          setRoomIsOpen(false)
          onParamsChange(
            { property: property?.slug },
            {
              roomCount: rooms,
              numberOfAdults: adults,
              numberOfChildren: children,
              numberOfInfants: infant,
            }
          )
        }}
        key={`${roomCount}${numberOfAdults}${numberOfChildren}${numberOfInfants}`}
      />
    </React.Fragment>
  )
}
